/* 
  
  MF Simulator: Main app entrypoint and routes
  
*/

import { useState, useLayoutEffect, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Main from './components/Main';
import Simulator from './components/Simulator';
import Orientation from './components/Orientation';
import PageNotFound from './components/PageNotFound';
import { injectOneTrust } from './utils/onetrust';


const App = (props) => {

  // Determine if we are debugging
  const queryParameters = new URLSearchParams(window.location.search)
  const dbg = queryParameters.get('dbg')
  const { i18n } = useTranslation();
  const [url, setUrl] = useState(null); // We don't know the language/url yet
  const location = useLocation();

  // Check if production domain (disable /en-us on prod only)
  const isProduction = window.location.hostname === 'mfsimulator.jnjvision.com';

  // Inject OneTrust when React Router navigates to /ja-jp (covers cases where
  // the user lands on another path first, e.g. / redirecting to /zh-hk on prod)
  useEffect(() => {
    injectOneTrust();
  }, [location.pathname]);

  // Get the referrer (just the domain). If there is no referrer (for example,
  // when going from HTTPS to HTTP - dev env), set to an empty string.
  // We prefer and trust the referrer in the header, and then the Url param.
  let referrer = '';
  try {
    referrer = (new URL(document.referrer)).hostname;
    dbg && console.log('Referrer detected in header:' + referrer);
  } catch(err) {
    dbg && console.log('No referrer detected in header:' + err);
  }
  
  // Detect the language before render
  // useLayoutEffect will block render until complete so be careful
  useLayoutEffect(() => {

    // Initialize language
    let language = '';

    // Detect the current language
    const detected = i18n.resolvedLanguage;
    dbg && console.log('Language detected as: ' + detected);

    // If this is a supported language, proceed, otherwise set to English
    if (detected in props.config['languages']) {
      dbg && console.log('Detected language is supported, setting to: ' + detected);
      language = detected;
    } else {
      dbg && console.log('Detected language is not supported, will default to English: ' + detected);
      language = 'en';
    }

    // Set the Url to the correct language Url so we can redirect them there, if required
    dbg && console.log('Url will be: ' + props.config['languages'][language]['url']);
    setUrl('/' + props.config['languages'][language]['url']);
    
  }, [dbg, props.config,  i18n.resolvedLanguage]);

  return (
    <Routes>
      {/* Conditional root routing: redirect to /zh-hk on prod */}
      {isProduction ? (
        <Route path='/' element={ <Navigate to='/zh-hk' replace /> } />
      ) : (
        <Route path='/' element={ <Navigate to={url} replace /> } />
      )}
      
      {/* Conditional /en-us routes: disabled on prod (404), enabled on staging/dev */}
      {isProduction ? (
        // Production: redirect /en-us routes to 404
        <Route path='/en-us/*' element={ <PageNotFound lan='en' hreflang='en-us' /> } />
      ) : (
        // Staging/Dev: /en-us routes enabled
        <>
          <Route path='/en-us' element={ <Main lan='en' hreflang='en-us' referrer={referrer} config={props.config} /> } />
          <Route path='/en-us/simulator' element={ <Simulator lan='en' hreflang='en-us' referrer={referrer} config={props.config} /> } />
          <Route path='/en-us/orientation' element={ <Orientation lan='en' hreflang='en-us' referrer={referrer} config={props.config} /> } />
        </>
      )}

      {/* Conditional /en-ca routes: disabled on prod (404), enabled on staging/dev */}
      {isProduction ? (
        <Route path='/en-ca/*' element={ <PageNotFound lan='en' hreflang='en-ca' /> } />
      ) : (
        <>
          <Route path='/en-ca' element={ <Main lan='en' hreflang='en-ca' referrer={referrer} config={props.config} /> } />
          <Route path='/en-ca/simulator' element={ <Simulator lan='en' hreflang='en-ca' referrer={referrer} config={props.config} /> } />
          <Route path='/en-ca/orientation' element={ <Orientation lan='en' hreflang='en-ca' referrer={referrer} config={props.config} /> } />
        </>
      )}

      {/* /ja-jp routes: enabled on all environments */}
      <Route path='/ja-jp' element={ <Main lan='ja-jp' hreflang='ja-jp' referrer={referrer} config={props.config} /> } />
      <Route path='/ja-jp/simulator' element={ <Simulator lan='ja-jp' hreflang='ja-jp' referrer={referrer} config={props.config} /> } />
      <Route path='/ja-jp/orientation' element={ <Orientation lan='ja-jp' hreflang='ja-jp' referrer={referrer} config={props.config} /> } />
      
      <Route path='/zh-hk' element={ <Main lan='zh' hreflang='zh-hk' referrer={referrer} config={props.config} /> } />
      <Route path='/zh-hk/simulator' element={ <Simulator lan='zh' hreflang='zh-hk' referrer={referrer} config={props.config} /> } />
      <Route path='/zh-hk/orientation' element={ <Orientation lan='zh' hreflang='zh-hk' referrer={referrer} config={props.config} /> } />

      <Route path='/fr-ca' element={ <Main lan='fr-ca' hreflang='fr-ca' referrer={referrer} config={props.config} /> } />
      <Route path='/fr-ca/simulator' element={ <Simulator lan='fr-ca' hreflang='fr-ca' referrer={referrer} config={props.config} /> } />
      <Route path='/fr-ca/orientation' element={ <Orientation lan='fr-ca' hreflang='fr-ca' referrer={referrer} config={props.config} /> } />
      
      <Route path="*" element={ <PageNotFound lan='en' hreflang='en-us' /> } />
    </Routes>
  );
}

export default App;